import { Button, DatePicker, Flex, Form, Input, Modal, Select, Card, Row, Col, Typography, Tag, Skeleton } from "antd";
import { useIntl } from "react-intl";
import { useEffect, useState } from "react";
import { IProjectRisk, IProjectRiskFormValues } from "../interfaces";
import { CONTENT_WIDTH, DATE_FORMATS, GUTTER_SIZE } from "@/enums";
import { useGetProjectImpactAreasQuery, useGetProjectRiskCategoriesQuery, useGetProjectRiskStatusesQuery, useGetUserQuery, useSuggestUsersQuery } from "@/services";
import { UserOptionLabel, ViperSelect } from "@/components";
import { ImpactSeverityLevel } from "../enums/ImpactSeverityLevel";
import { ProbabilitySeverityLevel } from "../enums/ProbabilitySeverityLevel";
import { mapPriorityValueToSeverityLevel } from "../helpers/severity-level";
import { PrioritySeverityPreview } from "./PrioritySeverityPreview";
import { toast } from "react-toastify";
import dayjs from "dayjs";

interface IProps {
  open: boolean;
  onClose: () => void;
  onOk: (values: IProjectRiskFormValues) => Promise<void>;
  onChangeToIssue?: () => Promise<void>;
  projectRisk: IProjectRisk | null | undefined;
}

export const AddEditRiskModal = ({ projectRisk, open, onClose, onOk, onChangeToIssue }: IProps) => {

  const [userQuery, setUserQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangingToIssue, setIsChangingToIssue] = useState(false);

  const { data: initialUser, isFetching: isFetchingInitialUser } = useGetUserQuery(
    projectRisk?.ownerId,
    {
      skip: !projectRisk?.ownerId
    }
  );

  const initialOwnerOption = initialUser && initialUser.id?.toLowerCase() === projectRisk?.ownerId?.toLowerCase()
    ? { label: initialUser.displayName, value: initialUser.id }
    : undefined;

  const { data: userSuggestions, isFetching: fetchingUserSuggestions } =
    useSuggestUsersQuery(
      { query: userQuery, viperOnly: true },
      {
        skip: !userQuery,
      }
    );

  const { data: statuses, isFetching: statusesLoading, isError: isStatusesError } = useGetProjectRiskStatusesQuery(null);
  const { data: categories, isFetching: categoriesLoading, isError: isCategoriesError } = useGetProjectRiskCategoriesQuery(null);
  const { data: impactAreas, isFetching: impactAreasLoading, isError: isImpactAreasError } = useGetProjectImpactAreasQuery(null);

  const statusOptions = (statuses || []).map(status => ({ value: status.id, label: status.name }));
  const categoryOptions = (categories || []).map(category => ({ value: category.id, label: category.name }));
  const impactAreaOptions = (impactAreas || []).map(impactArea => ({ value: impactArea.id, label: impactArea.name }));

  const intl = useIntl();

  useEffect(() => {
    if (!isStatusesError && !isCategoriesError && !isImpactAreasError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isStatusesError, isCategoriesError, isImpactAreasError]);


  const impactOptions = [
    { value: ImpactSeverityLevel.Low, label: ImpactSeverityLevel.Low + " = " + intl.formatMessage({ id: "risksAndIssues.severity.low" }) },
    { value: ImpactSeverityLevel.Medium, label: ImpactSeverityLevel.Medium + " = " + intl.formatMessage({ id: "risksAndIssues.severity.medium" }) },
    { value: ImpactSeverityLevel.High, label: ImpactSeverityLevel.High + " = " + intl.formatMessage({ id: "risksAndIssues.severity.high" }) },
    { value: ImpactSeverityLevel.Urgent, label: ImpactSeverityLevel.Urgent + " = " + intl.formatMessage({ id: "risksAndIssues.severity.urgent" }) }
  ];

  const probabilityOptions = [
    { value: ProbabilitySeverityLevel.Low, label: ProbabilitySeverityLevel.Low + " = " + intl.formatMessage({ id: "risksAndIssues.severity.low" }) },
    { value: ProbabilitySeverityLevel.Medium, label: ProbabilitySeverityLevel.Medium + " = " + intl.formatMessage({ id: "risksAndIssues.severity.medium" }) },
    { value: ProbabilitySeverityLevel.High, label: ProbabilitySeverityLevel.High + " = " + intl.formatMessage({ id: "risksAndIssues.severity.high" }) },
  ];

  const [form] = Form.useForm<IProjectRiskFormValues>();

  const impactValue = Form.useWatch("impactSeverityLevel", form);
  const probabilityValue = Form.useWatch("probabilitySeverityLevel", form);
  const priorityValue = impactValue * probabilityValue;

  const requiredMessage = (fieldName: string) => intl.formatMessage({ id: "validationErrors.missingRequiredField" }, { 
    fieldName: intl.formatMessage({ id: `risksAndIssues.formLabels.${fieldName}` })
   });
  const modalTitle = <>
    <Tag color="purple" bordered={false}>RISK</Tag>
    {projectRisk ? intl.formatMessage({ id: "risksAndIssues.editRisk" }) : intl.formatMessage({ id: "risksAndIssues.newRisk" })}
  </>;
  return (
    <Modal
      open={open}
      title={modalTitle}
      onCancel={onClose}
      okButtonProps={{ loading: isSubmitting }}
      onOk={async () => {
        if (isSubmitting) return;
        let values: IProjectRiskFormValues;
        try {
          values = await form.validateFields();
        } catch (error) {
          return;
        }
        let ownerId = values.ownerId as string | { label: string, value: string };
        if (typeof ownerId !== 'string') {
          ownerId = ownerId.value;
        }
        values.ownerId = ownerId;
        setIsSubmitting(true);
        await onOk(values);
        setIsSubmitting(false);
      }}
      okText={projectRisk ? intl.formatMessage({ id: "risksAndIssues.saveChanges" }) : intl.formatMessage({ id: "risksAndIssues.createRisk" })}
      footer={projectRisk ? (_, { OkBtn, CancelBtn }) => (
        <Flex justify="space-between">
          <Button
            data-test-id="project-risk-change-to-issue"
            color="primary"
            variant="outlined"
            loading={isChangingToIssue}
            onClick={async () => {
              if (!onChangeToIssue || isChangingToIssue) return;
              setIsChangingToIssue(true);
              try {
                await onChangeToIssue();
              } finally {
                setIsChangingToIssue(false);
              }
            }}
          >
            {intl.formatMessage({ id: "risksAndIssues.changeToIssue" })}
          </Button>
          <Flex gap="small">
            <CancelBtn />
            <OkBtn />
          </Flex>
        </Flex>
      ) : undefined}
      destroyOnHidden={true}
      style={{ minWidth: "50%" }}
    >
      <Form form={form}
        preserve={false}
        layout="vertical">
        <Form.Item
          name="description"
          label={intl.formatMessage({ id: "risksAndIssues.formLabels.description" })}
          initialValue={projectRisk?.description}
          rules={[
            { required: true, message: requiredMessage("description") },
            { type: 'string', whitespace: true, message: requiredMessage("description") }
          ]}
        >
          <Input
            placeholder={intl.formatMessage({ id: "risksAndIssues.formPlaceholders.description" })}
          />
        </Form.Item>
        <Form.Item
          name="preventativeAction"
          label={intl.formatMessage({ id: "risksAndIssues.formLabels.preventativeAction" })}
          initialValue={projectRisk?.preventativeAction}
        >
          <Input.TextArea
            placeholder={intl.formatMessage({ id: "risksAndIssues.formPlaceholders.preventativeAction" })}
          />
        </Form.Item>
        <Form.Item
          name="nextSteps"
          label={intl.formatMessage({ id: "risksAndIssues.formLabels.nextSteps" })}
          initialValue={projectRisk?.nextSteps}
        >
          <Input.TextArea
            placeholder={intl.formatMessage({ id: "risksAndIssues.formPlaceholders.nextSteps" })}
          />
        </Form.Item>
        <Form.Item
          name="history"
          label={intl.formatMessage({ id: "risksAndIssues.formLabels.history" })}
          initialValue={projectRisk?.history}
        >
          <Input.TextArea
            placeholder={intl.formatMessage({ id: "risksAndIssues.formPlaceholders.history" })}
           />
        </Form.Item>
        <Row gutter={GUTTER_SIZE.small}>
          <Col span={CONTENT_WIDTH.half}>
            <Form.Item
              name="projectRiskStatusId"
              label={intl.formatMessage({ id: "risksAndIssues.formLabels.projectRiskStatusId" })}
              initialValue={projectRisk?.projectRiskStatusId}
              rules={[
                { required: true, message: requiredMessage("projectRiskStatusId") }
              ]}
            >
              <Select
                options={statusOptions}
                loading={statusesLoading}
                value={form.getFieldValue("projectRiskStatusId")}
                onChange={(value) => form.setFieldsValue({ projectRiskStatusId: value })}
              />
            </Form.Item>
          </Col>
          <Col span={CONTENT_WIDTH.half}>
            <Form.Item
              name="projectRiskCategoryId"
              label={intl.formatMessage({ id: "risksAndIssues.formLabels.projectRiskCategoryId" })}
              initialValue={projectRisk?.projectRiskCategoryId}
              rules={[
                { required: true, message: requiredMessage("projectRiskCategoryId") }
              ]}
            >
              <Select
                options={categoryOptions}
                loading={categoriesLoading}
                value={form.getFieldValue("projectRiskCategoryId")}
                onChange={(value) => form.setFieldsValue({ projectRiskCategoryId: value })}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={GUTTER_SIZE.small}>
          <Col span={CONTENT_WIDTH.half}>
            <Form.Item
              name="projectImpactAreaId"
              label={intl.formatMessage({ id: "risksAndIssues.formLabels.projectImpactAreaId" })}
              initialValue={projectRisk?.projectImpactAreaId}
            >
              <Select
                allowClear
                options={impactAreaOptions}
                loading={impactAreasLoading}
                placeholder={intl.formatMessage({ id: "risksAndIssues.formPlaceholders.projectImpactAreaId" })}
                value={form.getFieldValue("projectImpactAreaId")}
                onChange={(value) => form.setFieldsValue({ projectImpactAreaId: value ?? null })}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item>
        <Card style={{ backgroundColor: '#fafafa' }} size="small" variant="borderless">
          <Typography.Title level={5}>
            {intl.formatMessage({ id: "risksAndIssues.formLabels.priorityCalculator" })}
          </Typography.Title>
          <Row gutter={GUTTER_SIZE.small}>
            <Col span={CONTENT_WIDTH.oneThird}>
              <Form.Item
                name="impactSeverityLevel"
                label={intl.formatMessage({ id: "risksAndIssues.formLabels.impactSeverityLevel" })}
                initialValue={projectRisk?.impactSeverityLevel}
                rules={[
                  { required: true, message: requiredMessage("impactSeverityLevel") }
                ]}
              >
                <Select
                  options={impactOptions}
                />
              </Form.Item>
            </Col>
            <Col flex="none">
            {/** Form item only used to align the multiplication symbol */}
              <Form.Item
                label=" "
              >
                <Flex justify="center"><span>&times;</span></Flex>
              </Form.Item>
            </Col>
            <Col span={CONTENT_WIDTH.oneThird}>
              <Form.Item
                name="probabilitySeverityLevel"
                label={intl.formatMessage({ id: "risksAndIssues.formLabels.probabilitySeverityLevel" })}
                initialValue={projectRisk?.probabilitySeverityLevel}
                rules={[
                  { required: true, message: requiredMessage("probabilitySeverityLevel") }
                ]}
              >
                <Select
                  options={probabilityOptions}
                />
              </Form.Item>
            </Col>
            <Col flex="none">
              <Form.Item
                label=" "
              >
                <Flex justify="center"><span>=</span></Flex>
              </Form.Item>
            </Col>
            <Col flex="auto">
              <Form.Item
                label={intl.formatMessage({ id: "risksAndIssues.formLabels.prioritySeverityLevel" })}
              >
                <PrioritySeverityPreview
                  value={priorityValue}
                  prioritySeverityLevel={mapPriorityValueToSeverityLevel(priorityValue)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
        </Form.Item>
        <Row gutter={GUTTER_SIZE.small}>
          <Col span={CONTENT_WIDTH.half}>
            {isFetchingInitialUser ? (
              <Form.Item
                label={intl.formatMessage({ id: "risksAndIssues.formLabels.ownerId" })}
                required
              >
                <Skeleton.Input block active />
              </Form.Item>
            ) : (
              <Form.Item
                name="ownerId"
                label={intl.formatMessage({ id: "risksAndIssues.formLabels.ownerId" })}
                initialValue={initialOwnerOption}
                rules={[{ required: true, message: requiredMessage("ownerId") }]}
              >
                <ViperSelect
                  onChange={(userId) => {
                    form.setFieldsValue({ ownerId: userId });
                  }}
                  optionLabelProp="label"
                  customOptions={userSuggestions?.map(
                    ({ document: { id, displayName, department, email, jobTitle } }) => (
                      <Select.Option key={id} label={displayName}>
                        <UserOptionLabel
                          displayName={displayName}
                          department={department}
                          email={email}
                          jobTitle={jobTitle}
                        />
                      </Select.Option>
                    )
                  )}
                  loading={fetchingUserSuggestions}
                  onFetchSuggestions={(searchTerm): void => {
                    setUserQuery(searchTerm);
                  }}
                  data-test-id="project-risk-owner-select"
                />
              </Form.Item>
            )}
          </Col>
          <Col span={CONTENT_WIDTH.half}>
            <Form.Item
              name="dateIdentified"
              label={intl.formatMessage({ id: "risksAndIssues.formLabels.dateIdentified" })}
              initialValue={projectRisk?.dateIdentified && dayjs(projectRisk?.dateIdentified)}
              rules={[{ required: true, message: requiredMessage("dateIdentified") }]}
            >
              <DatePicker
                format={DATE_FORMATS.fourDigitYear}
                placeholder={DATE_FORMATS.fourDigitYear}
                data-test-id="project-risk-date-identified-date-picker"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={CONTENT_WIDTH.half}>
            <Form.Item
              name="targetCompletionDate"
              label={intl.formatMessage({ id: "risksAndIssues.formLabels.targetCompletionDate" })}
              initialValue={projectRisk?.targetCompletionDate && dayjs(projectRisk?.targetCompletionDate)}
            >
              <DatePicker
                format={DATE_FORMATS.fourDigitYear}
                placeholder={DATE_FORMATS.fourDigitYear}
                data-test-id="project-risk-target-completion-date-date-picker"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};